import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";

import { SpecificationItem } from "../../components";
import { Button } from "../../components/ui";
import { isRcOrDaoStaff } from "../../core/config/storeId";
import { boxLabels, monthLabels } from "../../product/constants";

import EditProductImages from "./EditProductImages";
import ProductValue from "./ProductValue";
import PriceAnalytics from "./PriceAnalytics";
import { conditionDescription } from "../../constants";

const FieldList = ({ fields }) =>
  fields.map((obj) => <ProductValue {...obj} key={obj.name || obj.label} />);

const availabilityLabels = {
  available: "Available Now",
  preorder: "Pre-order",
  new_collection: "On Request",
};

const Specification = ({ listing, isProductPreview, user }) => {
  const history = useHistory();
  const [isEditing, setIsEditing] = useState(false);
  const product = listing.product;

  // Price analysis (retail/market price + evolution) is DAO Watches'
  // proprietary market intelligence — RC master admins or the DAO store only.
  const showPriceAnalysis = isRcOrDaoStaff(user);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);

    if (isEditing) {
      history.replace(
        `/request/order?preview=${product.id}&t=${dayjs().valueOf()}`
      );
    }
  };

  const addField = (list, label, name, value) => {
    if (isEditing || value || product?.values?.[name]?.value) {
      list.push({ label, value, name, product, isEditing });
    }
  };

  const priceFields = [];
  addField(priceFields, "Retail Price", "price_gbp");
  addField(priceFields, "Retail Price", "price_euro");

  const baseFields = [];
  addField(baseFields, "Title", "title");
  addField(baseFields, "Brand", null, product.brand.name);
  addField(baseFields, "Model", null, product.model.name);
  addField(baseFields, "Reference", null, product.reference_number);
  addField(baseFields, "Dial Color", "dial_color");
  addField(baseFields, "Case material", "case_material");
  addField(baseFields, "Case diameter", "case_diameter");
  addField(baseFields, "Case Glass", "case_glass");
  addField(baseFields, "Case Back", "case_back");
  addField(baseFields, "Water Resistance", "case_water");
  addField(baseFields, "Bracelet Type", "bracelet_type");
  addField(baseFields, "Clasp Type", "clasp_type");
  // Last, after the fixed attributes it supplements. Distinct from the
  // Movement block's "Additional Info" below (`movement_info`) — which is why
  // this is named for vintage rather than "Additional Details" as it first
  // shipped. addField() only pushes a field that has a value, so an empty one
  // renders nothing at all.
  addField(baseFields, "Vintage Details", "vintage_details");

  const productionFields = [];
  addField(productionFields, "Limitation", "limitation");
  if (isProductPreview) {
    addField(productionFields, "Year", "production_year");
  }

  const movementFields = [];
  addField(movementFields, "Reference", "movement_reference");
  addField(movementFields, "Movement: Quartz / Automatic", "movement");
  addField(movementFields, "Additional Info", "movement_info");
  addField(movementFields, "Power Reserve", "power_reserve");
  addField(movementFields, "Jewels", "movement_jewels");

  const availabilityText = listing.is_reserved
    ? "RESERVED"
    : availabilityLabels[listing.availability];

  return (
    <div className="specification">
      <div>
        {/* "Edit" toggles inline editing of the GLOBAL product record (product
            values + product images are shared across every store), so it is
            RC-master-admin only. `is_dao_admin` was just `is_admin && !is_merchant`,
            which let any tenant store admin edit shared product data. */}
        {isProductPreview && user?.is_master_admin && (
          <Button
            variant="secondary"
            fullWidth
            onClick={() => handleEditToggle()}
            data-btn="product-edit"
          >
            {isEditing ? "Stop editing" : "Edit"}
          </Button>
        )}
      </div>

      {!isProductPreview && (
        <>
          <h3>Details</h3>
          <SpecificationItem value={availabilityText} />
          <SpecificationItem
            label="Date"
            value={`${monthLabels[listing.month] || ""} ${listing.year ||
              ""}`.trim()}
          />
          <SpecificationItem
            label="Condition"
            value={listing.condition}
            tooltip={
              listing.condition ? conditionDescription[listing.condition] : null
            }
          />
          <SpecificationItem
            label="Delivery set"
            value={boxLabels[listing.box]}
          />
        </>
      )}

      {isProductPreview && showPriceAnalysis && (
        <>
          <h3 className="mt-4">Price Analysis</h3>
          <FieldList fields={priceFields} />

          <PriceAnalytics
            {...{
              isProductPreview,
              listing,
              isFirst: priceFields.length === 0,
              product,
            }}
          />
        </>
      )}

      {!isProductPreview && (
        <PriceAnalytics
          {...{
            isProductPreview,
            listing,
            isFirst: true,
            product,
          }}
        />
      )}

      <h3 className="mt-4">Specification</h3>
      <FieldList fields={baseFields} />

      {productionFields.length > 0 && (
        <>
          <h3 className="mt-4">Production</h3>
          <FieldList fields={productionFields} />
        </>
      )}

      {movementFields.length > 0 && (
        <>
          <h3 className="mt-4">Movement</h3>
          <FieldList fields={movementFields} />
        </>
      )}

      {isProductPreview &&
        (user?.is_admin || user?.edit_product_values_enabled) &&
        isEditing && <EditProductImages product={product} />}
    </div>
  );
};

export default Specification;
